// src/components/Vendors/VendorAnalyticsPanel.jsx

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/axios";
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import {
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  ShieldCheck,
  TrendingUp,
  Truck,
  Wallet,
  BadgePoundSterling,
  Search,
} from "lucide-react";

import VendorIntelligencePanel from "./VendorIntelligencePanel";

const currency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const compactCurrency = (value) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(Number(value || 0));

const percent = (value) => `${(Number(value || 0) * 100).toFixed(1)}%`;

const number = (value) => Number(value || 0).toLocaleString("en-GB");

function scoreClass(score) {
  const n = Number(score || 0);

  if (n >= 85) return "text-emerald-300";
  if (n >= 70) return "text-amber-300";

  return "text-red-300";
}

function riskClass(risk) {
  const safe = String(risk || "").toLowerCase();

  if (safe === "low") {
    return "border-emerald-400/30 bg-emerald-500/10 text-emerald-300";
  }

  if (safe === "medium") {
    return "border-amber-400/30 bg-amber-500/10 text-amber-300";
  }

  if (safe === "high") {
    return "border-red-400/30 bg-red-500/10 text-red-300";
  }

  return "border-white/10 bg-white/5 text-gray-300";
}

function Card({ children, className = "" }) {
  return (
    <div
      className={`rounded-2xl border border-white/10 bg-[#0f1115] shadow-xl shadow-black/20 ${className}`}
    >
      {children}
    </div>
  );
}

function KpiCard({
  icon: Icon,
  label,
  value,
  hint,
  tone = "text-white",
}) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="rounded-xl border border-white/10 bg-white/5 p-2">
          <Icon className="h-4 w-4 text-gray-300" />
        </div>

        {hint && (
          <div className="max-w-[130px] text-right text-[11px] leading-4 text-gray-500">
            {hint}
          </div>
        )}
      </div>

      <div className="mt-4 text-xs uppercase tracking-wide text-gray-500">
        {label}
      </div>

      <div className={`mt-1 text-2xl font-bold ${tone}`}>
        {value}
      </div>
    </Card>
  );
}

function EmptyChartState({ message }) {
  return (
    <div className="flex h-[280px] items-center justify-center rounded-xl border border-dashed border-white/10 bg-black/10 px-6 text-center">
      <div>
        <BarChart className="mx-auto h-7 w-7 text-gray-600" />
        <p className="mt-3 text-sm text-gray-500">{message}</p>
      </div>
    </div>
  );
}

export default function VendorAnalyticsPanel() {
  const [scorecards, setScorecards] = useState([]);
  const [performance, setPerformance] = useState([]);
  const [priceComparison, setPriceComparison] = useState([]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");

  const [search, setSearch] = useState("");
  const [selectedVendor, setSelectedVendor] = useState(null);

  const fetchData = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError("");

    try {
      const [
        scorecardsRes,
        performanceRes,
        priceComparisonRes,
      ] = await Promise.all([
        api.get("/analytics/vendors/executive-scorecards", {
          params: {
            _ts: Date.now(),
          },
        }),
        api.get("/analytics/vendor-performance", {
          params: {
            _ts: Date.now(),
          },
        }),
        api.get("/analytics/vendors/product-price-comparison", {
          params: {
            _ts: Date.now(),
          },
        }),
      ]);

      const scorecardRows = Array.isArray(scorecardsRes.data?.vendors)
        ? scorecardsRes.data.vendors
        : [];

      const performanceRows = Array.isArray(performanceRes.data)
        ? performanceRes.data
        : Array.isArray(performanceRes.data?.rows)
          ? performanceRes.data.rows
          : [];

      const priceRows = Array.isArray(priceComparisonRes.data?.rows)
        ? priceComparisonRes.data.rows
        : Array.isArray(priceComparisonRes.data)
          ? priceComparisonRes.data
          : [];

      setScorecards(scorecardRows);
      setPerformance(performanceRows);
      setPriceComparison(priceRows);
    } catch (err) {
      console.error("Failed to load vendor analytics:", err);

      setScorecards([]);
      setPerformance([]);
      setPriceComparison([]);

      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load vendor intelligence."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredVendors = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return scorecards;

    return scorecards.filter((vendor) => {
      const haystack = [
        vendor.vendor_name,
        vendor.vendor_id,
        vendor.risk_level,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return haystack.includes(q);
    });
  }, [scorecards, search]);

  const executiveTotals = useMemo(() => {
    const totalSpend = scorecards.reduce(
      (sum, vendor) =>
        sum + Number(vendor.total_po_value || 0),
      0
    );

    const outstanding = scorecards.reduce(
      (sum, vendor) =>
        sum + Number(vendor.outstanding_total || 0),
      0
    );

    const averageScore =
      scorecards.length > 0
        ? scorecards.reduce(
            (sum, vendor) =>
              sum + Number(vendor.scores?.overall || 0),
            0
          ) / scorecards.length
        : 0;

    const highRisk = scorecards.filter(
      (vendor) =>
        String(vendor.risk_level || "").toLowerCase() === "high"
    ).length;

    const lowRisk = scorecards.filter(
      (vendor) =>
        String(vendor.risk_level || "").toLowerCase() === "low"
    ).length;

    return {
      totalSpend,
      outstanding,
      averageScore,
      highRisk,
      lowRisk,
      vendorCount: scorecards.length,
    };
  }, [scorecards]);

  const topSpendVendors = useMemo(
    () =>
      [...scorecards]
        .sort(
          (a, b) =>
            Number(b.total_po_value || 0) -
            Number(a.total_po_value || 0)
        )
        .slice(0, 8)
        .map((vendor) => ({
          ...vendor,
          spendLabel: compactCurrency(vendor.total_po_value),
        })),
    [scorecards]
  );

  const bestPriceOpportunities = useMemo(
    () =>
      [...priceComparison]
        .filter(
          (row) => Number(row.saving_vs_best || 0) > 0
        )
        .sort(
          (a, b) =>
            Number(b.saving_vs_best || 0) -
            Number(a.saving_vs_best || 0)
        )
        .slice(0, 10),
    [priceComparison]
  );

  const bestVendor = useMemo(() => {
    return [...scorecards].sort(
      (a, b) =>
        Number(b.scores?.overall || 0) -
        Number(a.scores?.overall || 0)
    )[0];
  }, [scorecards]);

  const riskMessage =
    executiveTotals.highRisk > 0
      ? `${executiveTotals.highRisk} supplier(s) need attention.`
      : scorecards.length > 0
        ? "No high-risk suppliers currently flagged."
        : "No supplier scorecard data available.";

  if (selectedVendor) {
    return (
      <VendorIntelligencePanel
        vendorId={selectedVendor}
        onBack={() => setSelectedVendor(null)}
      />
    );
  }

  if (loading) {
    return (
      <Card className="p-6 text-white">
        <div className="flex items-center gap-3 text-gray-300">
          <RefreshCw className="h-5 w-5 animate-spin" />
          Loading vendor intelligence...
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="overflow-hidden text-white">
        <div className="border-b border-red-400/20 bg-red-500/10 p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-xl border border-red-400/20 bg-red-500/10 p-2">
              <AlertTriangle className="h-5 w-5 text-red-300" />
            </div>

            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-semibold text-red-200">
                Vendor intelligence could not be loaded
              </h2>

              <p className="mt-1 text-sm text-red-200/70">
                {error}
              </p>

              <p className="mt-2 text-xs text-gray-500">
                The page has not treated this connection problem as an empty
                vendor dataset.
              </p>
            </div>

            <button
              type="button"
              onClick={() => fetchData()}
              className="inline-flex items-center gap-2 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-2 text-sm font-semibold text-red-200 hover:bg-red-500/20"
            >
              <RefreshCw className="h-4 w-4" />
              Retry
            </button>
          </div>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-6 text-white">
      <Card className="overflow-hidden">
        <div className="border-b border-white/10 bg-gradient-to-r from-white/10 to-transparent p-6">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-emerald-300">
                Executive Supplier Intelligence
              </div>

              <h2 className="mt-2 text-2xl font-bold">
                Vendor Intelligence Centre
              </h2>

              <p className="mt-2 max-w-3xl text-sm text-gray-400">
                Supplier performance, spend exposure, risk, delivery
                reliability and product price opportunities in one executive
                view.
              </p>
            </div>

            <button
              type="button"
              onClick={() => fetchData({ silent: true })}
              disabled={refreshing}
              className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-sm hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  refreshing ? "animate-spin" : ""
                }`}
              />
              {refreshing ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        <div className="grid gap-4 p-6 md:grid-cols-2 xl:grid-cols-5">
          <KpiCard
            icon={ShieldCheck}
            label="Active Vendors"
            value={number(executiveTotals.vendorCount)}
            hint={`${executiveTotals.lowRisk} low risk`}
          />

          <KpiCard
            icon={TrendingUp}
            label="Average Score"
            value={`${executiveTotals.averageScore.toFixed(0)}/100`}
            tone={scoreClass(executiveTotals.averageScore)}
            hint={
              bestVendor
                ? `Best: ${bestVendor.vendor_name}`
                : "No vendor data"
            }
          />

          <KpiCard
            icon={Wallet}
            label="PO Spend"
            value={compactCurrency(executiveTotals.totalSpend)}
            hint="Total procurement value"
          />

          <KpiCard
            icon={BadgePoundSterling}
            label="Outstanding"
            value={compactCurrency(executiveTotals.outstanding)}
            tone="text-amber-300"
            hint="Invoice exposure"
          />

          <KpiCard
            icon={AlertTriangle}
            label="Risk"
            value={executiveTotals.highRisk}
            tone={
              executiveTotals.highRisk > 0
                ? "text-red-300"
                : "text-emerald-300"
            }
            hint={riskMessage}
          />
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-[1.1fr_0.9fr]">
        <Card className="p-5">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">
              Top Vendors by Spend
            </h3>

            <p className="text-xs text-gray-500">
              Spend concentration and supplier exposure.
            </p>
          </div>

          {topSpendVendors.length === 0 ? (
            <EmptyChartState message="No purchase order spend is available for the current vendor scope." />
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topSpendVendors}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />

                <XAxis
                  dataKey="vendor_name"
                  tick={{
                    fill: "#9ca3af",
                    fontSize: 11,
                  }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  tick={{
                    fill: "#9ca3af",
                    fontSize: 11,
                  }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) =>
                    compactCurrency(value)
                  }
                />

                <Tooltip
                  cursor={{
                    fill: "rgba(255,255,255,0.04)",
                  }}
                  contentStyle={{
                    background: "#111827",
                    border:
                      "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                  formatter={(value) => currency(value)}
                />

                <Bar
                  dataKey="total_po_value"
                  name="PO Spend"
                  fill="#34d399"
                  radius={[8, 8, 0, 0]}
                />

                <Bar
                  dataKey="outstanding_total"
                  name="Outstanding"
                  fill="#fbbf24"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold">
            Executive Decision Summary
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Fast interpretation of supplier health.
          </p>

          <div className="mt-5 space-y-3">
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="mt-0.5 h-5 w-5 shrink-0 text-emerald-300" />

                <div>
                  <div className="font-semibold">
                    Best Performing Supplier
                  </div>

                  <div className="mt-1 text-sm text-gray-400">
                    {bestVendor
                      ? `${bestVendor.vendor_name} currently leads with an overall score of ${
                          bestVendor.scores?.overall || 0
                        }/100.`
                      : "No supplier scorecard data available yet."}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />

                <div>
                  <div className="font-semibold">
                    Risk Attention
                  </div>

                  <div className="mt-1 text-sm text-gray-400">
                    {riskMessage}
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <div className="flex items-start gap-3">
                <BadgePoundSterling className="mt-0.5 h-5 w-5 shrink-0 text-blue-300" />

                <div>
                  <div className="font-semibold">
                    Price Opportunities
                  </div>

                  <div className="mt-1 text-sm text-gray-400">
                    {bestPriceOpportunities.length > 0
                      ? `${bestPriceOpportunities.length} product-level saving opportunities found.`
                      : "No cheaper supplier alternatives found in the current data."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold">
              Vendor Scorecards
            </h3>

            <p className="text-xs text-gray-500">
              Ranking suppliers by score, risk, spend, delivery and
              price movement.
            </p>
          </div>

          <div className="relative min-w-[260px]">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-500" />

            <input
              value={search}
              onChange={(event) =>
                setSearch(event.target.value)
              }
              placeholder="Search supplier..."
              className="w-full rounded-xl border border-white/10 bg-white/5 py-2 pl-9 pr-3 text-sm outline-none focus:border-emerald-400/50"
            />
          </div>
        </div>

        <div className="overflow-x-auto rounded-xl border border-white/10">
          <table className="min-w-full text-sm">
            <thead className="bg-white/[0.04] text-gray-300">
              <tr>
                <th className="p-3 text-left">
                  Vendor
                </th>
                <th className="p-3 text-right">
                  Score
                </th>
                <th className="p-3 text-left">
                  Risk
                </th>
                <th className="p-3 text-right">
                  Spend
                </th>
                <th className="p-3 text-right">
                  Outstanding
                </th>
                <th className="p-3 text-right">
                  On Time
                </th>
                <th className="p-3 text-right">
                  Rejection
                </th>
                <th className="p-3 text-right">
                  Price Volatility
                </th>
                <th className="p-3 text-right">
                  Action
                </th>
              </tr>
            </thead>

            <tbody>
              {filteredVendors
                .slice(0, 25)
                .map((vendor) => (
                  <tr
                    key={vendor.vendor_id}
                    className="border-t border-white/10 hover:bg-white/[0.03]"
                  >
                    <td className="p-3">
                      <div className="font-semibold">
                        {vendor.vendor_name}
                      </div>

                      <div className="text-xs text-gray-500">
                        {vendor.po_count || 0} POs ·{" "}
                        {vendor.invoice_count || 0} invoices
                      </div>
                    </td>

                    <td
                      className={`p-3 text-right font-bold ${scoreClass(
                        vendor.scores?.overall
                      )}`}
                    >
                      {vendor.scores?.overall ?? 0}/100
                    </td>

                    <td className="p-3">
                      <span
                        className={`inline-flex rounded-full border px-2 py-1 text-xs font-semibold ${riskClass(
                          vendor.risk_level
                        )}`}
                      >
                        {String(
                          vendor.risk_level || "unknown"
                        ).toUpperCase()}
                      </span>
                    </td>

                    <td className="p-3 text-right">
                      {currency(vendor.total_po_value)}
                    </td>

                    <td className="p-3 text-right text-amber-300">
                      {currency(
                        vendor.outstanding_total
                      )}
                    </td>

                    <td className="p-3 text-right text-emerald-300">
                      {percent(vendor.on_time_rate)}
                    </td>

                    <td className="p-3 text-right">
                      {percent(vendor.rejection_rate)}
                    </td>

                    <td className="p-3 text-right">
                      {percent(
                        vendor.price_volatility_rate
                      )}
                    </td>

                    <td className="p-3 text-right">
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedVendor(
                            vendor.vendor_id
                          )
                        }
                        className="whitespace-nowrap rounded-lg border border-emerald-400/30 bg-emerald-500/10 px-3 py-1 text-xs font-semibold text-emerald-300 hover:bg-emerald-500/20"
                      >
                        View Intelligence
                      </button>
                    </td>
                  </tr>
                ))}

              {filteredVendors.length === 0 && (
                <tr>
                  <td
                    colSpan={9}
                    className="p-6 text-center text-gray-400"
                  >
                    {search.trim()
                      ? "No vendors match your search."
                      : "No vendors are available in the current company and unit scope."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="grid gap-6 xl:grid-cols-2">
        <Card className="p-5">
          <div className="mb-4 flex items-center gap-2">
            <Truck className="h-5 w-5 text-emerald-300" />

            <div>
              <h3 className="text-lg font-semibold">
                Delivery Performance
              </h3>

              <p className="text-xs text-gray-500">
                On-time rate, complete delivery and rejection
                trend.
              </p>
            </div>
          </div>

          {performance.length === 0 ? (
            <EmptyChartState message="No receiving performance data is available for the current vendor scope." />
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={performance.slice(0, 8)}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="rgba(255,255,255,0.08)"
                />

                <XAxis
                  dataKey="vendor_name"
                  tick={{
                    fill: "#9ca3af",
                    fontSize: 11,
                  }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  tick={{
                    fill: "#9ca3af",
                    fontSize: 11,
                  }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(value) =>
                    `${Math.round(value * 100)}%`
                  }
                />

                <Tooltip
                  contentStyle={{
                    background: "#111827",
                    border:
                      "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 12,
                    color: "#fff",
                  }}
                  formatter={(value) => percent(value)}
                />

                <Bar
                  dataKey="on_time_rate"
                  name="On Time"
                  fill="#34d399"
                  radius={[8, 8, 0, 0]}
                />

                <Bar
                  dataKey="full_delivery_rate"
                  name="Full Delivery"
                  fill="#60a5fa"
                  radius={[8, 8, 0, 0]}
                />

                <Bar
                  dataKey="rejection_rate"
                  name="Rejection"
                  fill="#fb7185"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </Card>

        <Card className="p-5">
          <h3 className="text-lg font-semibold">
            Product Price Opportunities
          </h3>

          <p className="mt-1 text-xs text-gray-500">
            Products where another supplier appears cheaper based
            on available PO data.
          </p>

          <div className="mt-4 max-h-[280px] overflow-auto rounded-xl border border-white/10">
            {bestPriceOpportunities.length === 0 ? (
              <div className="flex items-center gap-2 p-4 text-emerald-300">
                <CheckCircle className="h-4 w-4 shrink-0" />
                No cheaper supplier alternatives found.
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead className="sticky top-0 bg-[#171a21] text-gray-300">
                  <tr>
                    <th className="p-3 text-left">
                      Product
                    </th>
                    <th className="p-3 text-left">
                      Vendor
                    </th>
                    <th className="p-3 text-right">
                      Current
                    </th>
                    <th className="p-3 text-right">
                      Best
                    </th>
                    <th className="p-3 text-right">
                      Saving
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {bestPriceOpportunities.map(
                    (row, index) => (
                      <tr
                        key={`${row.product_key}-${row.vendor_id}-${index}`}
                        className="border-t border-white/10 hover:bg-white/[0.03]"
                      >
                        <td className="p-3">
                          <div className="font-medium">
                            {row.description ||
                              row.item_code ||
                              row.product_key}
                          </div>

                          <div className="text-xs text-gray-500">
                            {row.item_code ||
                              row.product_key}
                          </div>
                        </td>

                        <td className="p-3">
                          {row.vendor_name ||
                            "Unknown vendor"}
                        </td>

                        <td className="p-3 text-right">
                          {currency(row.latest_price)}
                        </td>

                        <td className="p-3 text-right text-emerald-300">
                          {currency(row.best_price)}
                        </td>

                        <td className="p-3 text-right text-amber-300">
                          {currency(
                            row.saving_vs_best
                          )}
                        </td>
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}